import {
    APP_INITIALIZER,
    ApplicationConfig,
    importProvidersFrom,
    LOCALE_ID,
    provideZoneChangeDetection
} from '@angular/core';
import {provideRouter} from '@angular/router';
import {routes} from './app.routes';
import {provideAnimationsAsync} from '@angular/platform-browser/animations/async';
import {providePrimeNG} from 'primeng/config';
import Aura from '@primeng/themes/aura';
import {HttpClient, provideHttpClient, withFetch, withInterceptors} from "@angular/common/http";
import {ConfirmationService} from "primeng/api";
import {registerLocaleData} from "@angular/common";
import {TranslateHttpLoader} from "@ngx-translate/http-loader";
import localeEs from '@angular/common/locales/es';
import {AuthService} from "./shared/auth/services/auth.service";
import {PolicyModalService} from "./shared/auth/services/policy-modal.service";
import {TranslateLoader, TranslateModule, TranslateService} from "@ngx-translate/core";
import {ApiUsersService} from "./shared/api/api.users.service";
import {firstValueFrom} from "rxjs";
import {tokenRefreshInterceptor} from "./shared/auth/token-refresh-interceptor";
import {TextResourcesService} from "./shared/resources/text-resources.service";
import {DialogService} from "primeng/dynamicdialog";
import {
    resolveAppLocale,
    toTranslationLanguage,
} from './wappy/languages/language.config';

export function httpLoaderFactory(http: HttpClient) {
    return new TranslateHttpLoader(http, './i18n/', '.json');
}


export function appInitializer(
    authService: AuthService,
    apiUsersService: ApiUsersService,
    textResourcesService: TextResourcesService,
    translate: TranslateService,
    policyModalService: PolicyModalService,
): () => Promise<any> {
    return async () => {
        try {
            await authService.init();
        } catch (error) {
            console.error('Error inicializando auth:', error);
            return;
        }

        try {
            const user = await firstValueFrom(apiUsersService.getMe());
            authService.setApiUser(user);
            const locale = resolveAppLocale(user.locale);
            await firstValueFrom(
                translate.use(toTranslationLanguage(locale)),
            );

            if (!user.hasLatestPolicyAccepted) {
                policyModalService.open(
                    user.hasAnyAcceptedPolicy ? 'update' : 'first-time',
                );
            }
        } catch (error) {
            console.error('Error obteniendo usuario:', error);
        }

        try {
            await firstValueFrom(textResourcesService.loadOnce());
        } catch (error) {
            console.error('Error obteniendo recursos:', error);
        }
    };
}

registerLocaleData(localeEs, 'es-ES');

export const appConfig: ApplicationConfig = {
    providers: [
        provideZoneChangeDetection({eventCoalescing: true}),
        provideRouter(routes),
        provideHttpClient(withFetch(), withInterceptors([tokenRefreshInterceptor])),
        provideAnimationsAsync(),
        providePrimeNG({
            theme: {
                preset: Aura,
                options: {
                    prefix: 'p',
                    darkModeSelector: '.my-app-dark',
                    cssLayer: {
                        name: 'primeng',
                        order: 'tailwind-base, primeng, tailwind-utilities'
                    }
                }
            },
            ripple: true,
            inputVariant: 'filled'
        }),
        {
            provide: APP_INITIALIZER,
            useFactory: appInitializer,
            deps: [
                AuthService,
                ApiUsersService,
                TextResourcesService,
                TranslateService,
                PolicyModalService,
            ],
            multi: true,
        },
        importProvidersFrom(
            TranslateModule.forRoot({
                loader: {
                    provide: TranslateLoader,
                    useFactory: httpLoaderFactory,
                    deps: [HttpClient],
                },
            })
        ),
        { provide: LOCALE_ID, useValue: 'es-ES' },
        ConfirmationService,
        DialogService,
    ],
};
